import { createRef, Component } from 'react'
import PropTypes from 'prop-types'
import Panel from 'beam/dist/Panel'
import { PopupWindow } from 'beam-v11'
import withStyles from 'beam/dist/styles'
import { ConditionalWrap } from 'beam/dist/utils'
import Typography from 'beam/dist/Typography'
import IconButton from 'beam/dist/IconButton'
import BeamIcon from 'beam/dist/BeamIcon'
import classNames from 'classnames'
import { withTranslation } from 'react-i18next'
import { CircularProgress } from 'beam/dist/Progress'
import ReactPlayer from 'react-player'
import { isEmpty } from 'lodash'
import { difference } from '../../../Common/Utils'
import { detect } from 'detect-browser'
import popupTemplateUrl from './window.html'

const dataTestIdPrefix = 'App-Recordings-MediaPlayer-ScreenCapture-'

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '& *': {
      boxSizing: 'border-box',
    },
  },
  errorColor: {
    color: theme.palette.error.main,
  },
  panelRoot: {
    flex: '1 1 auto',
  },
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    height: `calc(100% - ${theme.spacing(4)}px)`,
    backgroundColor: theme.palette.grey[900],
  },
  loading: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.grey[900],
  },
  errorContainer: {
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    textAlign: 'center',
  },
  videoPlayerRoot: {
    flex: '1 1 auto',
    display: 'block',
    paddingLeft: '61.8%', // golden ratio
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: `padding ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
    '& video': {
      objectFit: 'contain',
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: '-38.2%',
      width: `calc(38.2% - ${theme.spacing(2)}px)`,
      background: 'rgba(0,0,0,0.5)',
      height: '100%',
      transition: `right ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
      zIndex: 3,
    },
  },
  videoPlayerRootIsHovered: {
    '&:before': {
      right: theme.spacing(2),
    },
    '& $videoPlayerColumn': {
      marginLeft: 0,
    },
  },
  videoPlayerColumn: {
    // display: 'flex',
    // flexDirection: 'column',
    marginLeft: '100%',
    padding: '8px',
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: `margin ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
  },
  player: {
    // flex: '1 1 auto',
    width: '100%',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 3,
    // height: 'calc(100% / 3)',
    '&:not(:last-child)': {
      marginBottom: '8px',
    },
    '& video': {
      width: '100%',
      // Required to override height issues with react player
      height: 'inherit !important',
    },
  },
  playerMain: {
    position: 'absolute',
    top: '0',
    left: '0',
    height: '100%',
    width: 'calc(100% - 16px)',
    cursor: 'default',
    zIndex: 0,
    '& video': {
      height: '100%',
    },
  },
})

const STORAGE_KEY = 'ScreenCapturePanel_Docked'

class ScreenCapture extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    /** if the data is still being loaded */
    loading: PropTypes.bool.isRequired,
    /** if loading the data failed for some reason */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    /** response from api storagefile */
    videoData: PropTypes.object,
    /** Controls whether the video is playing or paused */
    isPlaying: PropTypes.bool.isRequired,
    /** Controls the playback rate */
    playbackRate: PropTypes.number.isRequired,
    /** Time to seek the video to in milliseconds */
    currentTime: PropTypes.number,
    /** Callback for when video is ready to play */
    onCanPlay: PropTypes.func.isRequired,
    /** Callback for time updates */
    onTimeUpdate: PropTypes.func.isRequired,
    /** if user declined to show video */
    videoDeclined: PropTypes.bool,
    /** Controls whether screen capture is loading or not */
    isDocked: PropTypes.bool.isRequired,
    /** Callback called when the ALL screen recordings fail to load for a contact */
    onVideoHasErrors: PropTypes.func,
    setDocked: PropTypes.func.isRequired,
    arePopupsEnabled: PropTypes.bool,
    onPopupError: PropTypes.func.isRequired,
    onPopupsBlocked: PropTypes.func.isRequired,
    videoFormat: PropTypes.string.isRequired,
    videoUploadFailed: PropTypes.func,
  }

  static defaultProps = {
    videoData: {},
    arePopupsEnabled: true,
  }

  static OFFSET_THRESHOLD = 300

  state = {
    mainScreenIndex: 0,
    anyVideoLoaded: false,
    videoUrls: [],
    isHovered: false,
  }

  hasProgressedAfterDockUndock = false
  unmounting = false
  // create refs to store the video container DOM elements
  dockTime = 0
  mediaPromise = new Promise(resolve => resolve()) // Prevent play and pause requests being fired before the other has completed
  browser = detect()

  componentDidMount() {
    this.setVideoUrls()

    const lastState = window.localStorage.getItem(STORAGE_KEY)
    if (lastState) {
      this.props.setDocked(JSON.parse(lastState))
    }
  }

  componentWillUnmount() {
    this.unmounting = true
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged = !isEmpty(difference(nextProps, this.props))
    const stateChanged = !isEmpty(difference(nextState, this.state))
    return propsChanged || stateChanged
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.videoData !== this.props.videoData ||
      (this.props.arePopupsEnabled && prevProps.isDocked !== this.props.isDocked)
    ) {
      this.setVideoUrls()
    }
    if (Math.abs(prevProps.currentTime - this.props.currentTime) > ScreenCapture.OFFSET_THRESHOLD) {
      this.updateCurrentTime(this.props.currentTime)
    }
  }

  getVideoUrls(videoData) {
    const formats = Object.keys(videoData).filter(format => format === this.props.videoFormat)

    if (formats.length === 0) {
      return []
    }

    /**
    * This iterates over data structured in this format:
      video: {
        mp4: [
          { url: '/api/rest/recording/media/play/769580?type=mp4' }
          { url: '/api/rest/recording/media/play/769581?type=mp4' }
          { url: '/api/rest/recording/media/play/769582?type=mp4' }
        ],
        webmv: [
          { url: '/api/rest/recording/media/play/769580?type=webmv' }
          { url: '/api/rest/recording/media/play/769581?type=webmv' }
          { url: '/api/rest/recording/media/play/769582?type=webmv' }
        ],
        webmvvp9: [
          { url: '/api/rest/recording/media/play/769580?type=webmvvp9' }
          { url: '/api/rest/recording/media/play/769581?type=webmvvp9' }
          { url: '/api/rest/recording/media/play/769582?type=webmvvp9' }
        ],
        webmvvp8: [
          { url: '/api/rest/recording/media/play/769580?type=webmvvp8' }
          { url: '/api/rest/recording/media/play/769581?type=webmvvp8' }
          { url: '/api/rest/recording/media/play/769582?type=webmvvp8' }
        ],
        webm: [
          { url: '/api/rest/recording/media/play/769580?type=webm' }
          { url: '/api/rest/recording/media/play/769581?type=webm' }
          { url: '/api/rest/recording/media/play/769582?type=webm' }
        ],
        m4v: [
          { url: '/api/rest/recording/media/play/769580?type=m4v' }
          { url: '/api/rest/recording/media/play/769581?type=m4v' }
          { url: '/api/rest/recording/media/play/769582?type=m4v' }
        ]
      }
     * turning it into this format:
      videoUrls:[
        [
          { url: '/api/rest/recording/media/play/769580?type=mp4',      format: 'mp4',      hasError: false },
          { url: '/api/rest/recording/media/play/769580?type=webmv',    format: 'webmv',    hasError: false },
          { url: '/api/rest/recording/media/play/769580?type=webmvvp9', format: 'webmvvp9', hasError: false },
          { url: '/api/rest/recording/media/play/769580?type=webmvvp8', format: 'webmvvp8', hasError: false },
          { url: '/api/rest/recording/media/play/769580?type=webm',     format: 'webm',     hasError: false },
          { url: '/api/rest/recording/media/play/769580?type=m4v',      format: 'm4v',      hasError: false },
        ],[
          { url: '/api/rest/recording/media/play/769581?type=mp4',      format: 'mp4',      hasError: false },
          { url: '/api/rest/recording/media/play/769581?type=webmv',    format: 'webmv',    hasError: false },
          { url: '/api/rest/recording/media/play/769581?type=webmvvp9', format: 'webmvvp9', hasError: false },
          { url: '/api/rest/recording/media/play/769581?type=webmvvp8', format: 'webmvvp8', hasError: false },
          { url: '/api/rest/recording/media/play/769581?type=webm',     format: 'webm',     hasError: false },
          { url: '/api/rest/recording/media/play/769581?type=m4v',      format: 'm4v',      hasError: false },
        ],[
          ...
        ]
      ]
     */
    return videoData[formats[0]].map((_, screenNumber) =>
      formats.reduce(
        (videoUrls, format) => [
          ...videoUrls,
          {
            hasError: false,
            url: videoData[format][screenNumber].url,
            format,
          },
        ],
        [],
      ),
    )
  }

  setVideoUrls = () => {
    const { videoData } = this.props
    const videoUrls = this.getVideoUrls(videoData)

    if (!this.references) {
      this.references = videoUrls.map(() => createRef())
    }

    this.setState({
      videoUrls,
      anyVideoLoaded: false,
    })
  }

  updateCurrentTime = milliseconds => {
    // convert to seconds for HTMLMediaElement API
    const sec = milliseconds / 1000
    this.references &&
      this.references.forEach(ref => {
        if (ref.current && !isNaN(ref.current.getDuration())) {
          // See: https://github.com/CookPete/react-player#instance-methods
          ref.current.seekTo(parseFloat(sec), 'seconds')
        }
      })
  }

  handleMouseOver = () => {
    this.setState({ isHovered: true })
  }

  handleMouseOut = () => {
    this.setState({ isHovered: false })
  }

  handleProgress = state => {
    if (this.hasProgressedAfterDockUndock) {
      this.hasProgressedAfterDockUndock = false
      this.updateCurrentTime(this.getCurrentTime())
      this.props.onTimeUpdate(this.getCurrentTime() + 250) // allow 250ms wiggle room since the audio keeps playing during dock/undock
    } else {
      const milliseconds = parseInt(state.playedSeconds * 1000, 10)
      this.props.onTimeUpdate(milliseconds)
    }
  }

  handleScreenClick = index => {
    this.setState({
      mainScreenIndex: index,
    })
  }

  handleDocking = () => {
    const { setDocked } = this.props
    setDocked(true)

    if (!this.unmounting) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(true))
    }

    this.dockTime = this.getCurrentTime()
    this.updatePlayerForDockUndock()
  }

  handlePopOut = () => {
    const { arePopupsEnabled, onPopupsBlocked, setDocked } = this.props

    if (!arePopupsEnabled) {
      onPopupsBlocked()
    } else {
      setDocked(false)
      if (!this.unmounting) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(false))
      }

      this.dockTime = this.getCurrentTime()
      this.updatePlayerForDockUndock()
    }
  }

  handlePopupError = () => {
    const { onPopupError } = this.props
    onPopupError()
    this.handleDocking()
  }

  handleReady = () => {
    const { onCanPlay } = this.props
    const anyVideoReady = this.references && this.references.some(ref => ref.current && ref.current.player.isReady)

    this.setState({
      anyVideoLoaded: anyVideoReady,
    })

    if (anyVideoReady) {
      onCanPlay()
    }
  }

  /**
   * Media player state needs to be updated when the capture window is docked or undocked
   */
  updatePlayerForDockUndock = () => {
    this.hasProgressedAfterDockUndock = true
    this.updateCurrentTime(this.dockTime)
  }

  getCurrentTime = () => {
    const time =
      this.references &&
      this.references.reduce((max, ref) => {
        if (ref.current && !isNaN(ref.current.duration)) {
          // TODO IE11 error
          const refTime = ref.current.getCurrentTime()

          return max > refTime ? max : refTime
        }

        return max
      }, 0) * 1000

    return time > 0 ? time : this.props.currentTime
  }

  // eslint-disable-next-line no-unused-vars -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
  handlePopupClose = (_ = null, wasClosedByParent = false) => {
    if (!wasClosedByParent) {
      this.handleDocking()
    }
  }

  setErrorOnFailedScreen = source => error => {
    const { onVideoHasErrors } = this.props
    this.setState(prevState => ({
      videoUrls: prevState.videoUrls.map(videoFormats =>
        // http://jira/browse/SAAS-64113 - false positive error (AbortError) during audio/video scrubbing
        videoFormats.map(video => ({ ...video, hasError: (video.hasError || video.url === source.url) && error.name !== 'AbortError' })),
      ),
    }))

    const allVideosFailed = this.state.videoUrls.filter(vidUrlObj => vidUrlObj[0].hasError === true)
    if (allVideosFailed) {
      onVideoHasErrors()
    }
  }

  preventContextMenu = e => e.preventDefault()

  renderPlayer = (sources, index) => {
    const { isPlaying, playbackRate, classes, t } = this.props
    const { mainScreenIndex, videoUrls } = this.state

    const hasMultipleScreens = videoUrls.length > 1
    const firstVideoWithoutError = videoUrls && videoUrls.find(vidUrlObj => vidUrlObj[0].hasError === false)

    return (
      <div
        tabIndex={0}
        role="button"
        key={index}
        className={classNames(classes.player, {
          [classes.playerMain]: !hasMultipleScreens || (hasMultipleScreens && mainScreenIndex === index),
        })}
        onClick={() => this.handleScreenClick(index)}
        onKeyPress={() => this.handleScreenClick(index)}
      >
        {!sources[0].hasError && (
          <ReactPlayer
            ref={this.references[index]}
            url={sources[0].url}
            width="100%"
            height="inherit"
            // Needs to be muted as of Chrome 66
            // If the screen is undocked automatically, for some reason, Chrome thinks it is being auto-played
            // and Chrome won't auto-play media with audio
            // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
            muted={true}
            playing={isPlaying}
            playbackRate={playbackRate}
            onReady={this.handleReady}
            onProgress={index === videoUrls.indexOf(firstVideoWithoutError) ? this.handleProgress : null}
            controls={false}
            onContextMenu={this.preventContextMenu}
            onError={this.setErrorOnFailedScreen(sources[0])}
          />
        )}
        {sources[0].hasError && (
          <div className={classes.errorContainer}>
            <div style={{ padding: '15%' }}>
              <BeamIcon style={{ width: '100%' }} fontSize="large" className={classes.errorColor}>
                AlertCircle
              </BeamIcon>
              <Typography className={classes.errorColor}>{t('failedToLoadThisScreenRecording')}</Typography>
            </div>
          </div>
        )}
      </div>
    )
  }

  renderFailedUpload = () => {
    const { t } = this.props
    return (
      <Typography color="secondary" align="center">
        {t('videoRestoreUnsuccessful')}
      </Typography>
    )
  }

  renderNoData = () => {
    const { t } = this.props
    return (
      <Typography color="secondary" align="center">
        {t('noVideoData')}
      </Typography>
    )
  }

  renderLoading = () => {
    const { classes } = this.props
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    )
  }

  renderError = () => {
    const { error } = this.props
    return (
      <Typography variant="body1" color="error" align="center">
        {error.toString()}
      </Typography>
    )
  }

  render() {
    const { t, classes, loading, error, arePopupsEnabled, isDocked, videoDeclined, videoUploadFailed } = this.props
    const { anyVideoLoaded, videoUrls, isHovered } = this.state

    const headerActions =
      this.browser.name === 'ie' ? null : (
        <IconButton color="inherit" onClick={isDocked ? this.handlePopOut : this.handleDocking}>
          <BeamIcon>{isDocked ? 'OpenInNew' : 'OpenInApp'}</BeamIcon>
        </IconButton>
      )

    return (
      <ConditionalWrap
        condition={arePopupsEnabled && !isDocked}
        wrap={children => (
          <PopupWindow
            id="screen-capture-window"
            title={t('screenCapture')}
            willObserveMutations={false}
            useParentUrl={false}
            templateURL={popupTemplateUrl}
            onError={this.handlePopupError}
            onClose={this.handlePopupClose}
            errorText={t('popupBlockerEnabled')}
          >
            {children}
          </PopupWindow>
        )}
      >
        <div className={classes.root}>
          <Panel
            titleIcon={<BeamIcon>MonitorMultiple</BeamIcon>}
            titleIconTooltipText={t('screenCapture')}
            title={t('screenCapture')}
            dense
            variant="light"
            dataTestId={`${dataTestIdPrefix}panel`}
            headerTestId={`${dataTestIdPrefix}panel-heading`}
            headerActions={headerActions}
            classes={{ root: classes.panelRoot, body: classes.panelBody }}
          >
            {!loading && !error && videoUrls.length > 0 && (
              <div
                onMouseOver={this.handleMouseOver}
                onFocus={this.handleMouseOver}
                onMouseOut={this.handleMouseOut}
                onBlur={this.handleMouseOut}
                className={classNames(classes.videoPlayerRoot, {
                  [classes.videoPlayerRootIsHovered]: isHovered && videoUrls.length > 1,
                })}
              >
                <ConditionalWrap
                  condition={videoUrls.length > 1}
                  wrap={children => <div className={classes.videoPlayerColumn}>{children}</div>}
                >
                  {videoUrls.map(this.renderPlayer)}
                </ConditionalWrap>
              </div>
            )}
            {(videoUploadFailed && this.renderFailedUpload()) ||
              (videoDeclined && this.renderNoData()) ||
                ((loading ||
                  (videoUrls.length !== 0 &&
                    !anyVideoLoaded &&
                    !videoUrls.every(vidUrlObj => vidUrlObj[0].hasError === true))) &&
                  this.renderLoading()) ||
                (error && this.renderError()) ||
                (videoUrls.length === 0 && this.renderNoData())}
          </Panel>
        </div>
      </ConditionalWrap>
    )
  }
}

export { ScreenCapture as UnstyledScreenCapture }
export default withTranslation()(withStyles(styles)(ScreenCapture))
